<template>
  <div
    :class="classes"
    :style="borderColor ? { borderColor } : undefined"
    :role="onClick ? 'button' : undefined"
    :data-i="icon"
    @click="handleClick"
  >
    <slot />
  </div>
</template>

<script lang="ts">
export const variants = [
  'warning',
  'error',
  'secondary',
  'light',
  'grey',
  'blue',
  'green',
  'transparent',
  'muted',
] as const;

export type CalloutVariant = (typeof variants)[number];
</script>

<script setup lang="ts">
import { computed, useCssModule } from 'vue';
import { useTrack } from '@/composables/plugins';

export type Props = {
  state?: 'active' | 'focus';
  variant?: CalloutVariant;
  onClick?: (event: Event) => void;
  icon?: string;
  tid?: string;
  bordered?: boolean;
  noBorder?: boolean;
  borderColor?: string;
  darkIcon?: boolean;
  compact?: boolean;
  noMargin?: boolean;
};
const props = withDefaults(defineProps<Props>(), { variant: 'light' });

const style = useCssModule();
const classes = computed(() => {
  return {
    [style.callout]: true,
    [style[props.variant ?? '']]: props.variant,
    [style[props.state ?? '']]: props.state,
    [style.bordered]: props.bordered === true,
    [style['no-border']]: props.noBorder === true,
    [style.darkIcon]: props.darkIcon,
    [style.compact]: props.compact,
    [style.noMargin]: props.noMargin,
    'cursor-pointer': !!props.onClick,
  };
});

const track = useTrack();
const trackClick = () => {
  if (!props.tid) return;
  track('card-click', { tid: props.tid });
};

const handleClick = (event: Event) => {
  if (!props.onClick) return;
  trackClick();
  props.onClick(event);
};
</script>

<style module>
.callout {
  position: relative;
  padding: 1rem;
  margin: 1rem 0;
  border: 1px solid transparent;
  transition: border-color 0.1s ease-in-out;

  &.compact {
    padding: 0.5rem;
  }

  &.noMargin {
    margin: 0;
  }

  &[data-i] {
    display: flex;
    align-items: center;
  }

  &[data-i]::before {
    line-height: 1;
    font-size: var(--fontSize);
    left: 1em;
    margin-right: 0.75em;
  }

  background-color: rgb(0 0 0 / 5%);
  border-radius: 10px;

  p:first-child {
    margin-top: 0;
  }

  p:last-child {
    margin-bottom: 0;
  }

  &.warning {
    background-color: var(--amber-100);
    color: var(--neutral-800);

    &[data-i]::before {
      color: var(--amber-600);
    }

    &.bordered {
      border-color: var(--amber-400);
    }
  }

  &.error {
    background-color: var(--error-weak);
    color: var(--neutral-900);

    &[data-i]::before {
      color: var(--error);
    }

    &.bordered {
      border-color: var(--error-fin);
    }
  }

  &.secondary {
    background-color: var(--neutral-200);
    border-color: var(--neutral-400);
  }

  &.light {
    background-color: var(--color-white);
    border-color: var(--callout-light-border, var(--neutral-400));
    color: var(--neutral-800);

    &[data-i]::before {
      color: var(--neutral-800);
    }
  }

  &.blue {
    background-color: var(--blue-100);
    border-color: var(--blue-400);
    color: var(--neutral-800);

    &[data-i]::before {
      color: var(--blue-700);
    }
  }

  &.green {
    background-color: var(--green-100);
    border-color: var(--green-400);
    color: var(--neutral-800);

    &[data-i]::before {
      color: var(--green-700);
    }
  }

  &.transparent {
    background-color: transparent;
    border-color: var(--neutral-400);
  }

  &.transparent,
  &.light {
    &[role='button'].active {
      border-color: var(--amber-600);
    }

    &[role='button'].focus {
      border-color: var(--amber-600);
      border-style: dashed;
    }

    &[role='button']:hover {
      border-color: var(--amber-100);
    }
  }

  &.grey {
    background-color: var(--neutral-200);

    &[role='button'].active {
      background-color: var(--amber-100);
    }

    &[role='button'].focus {
      background-color: var(--amber-100);
      border-style: dashed;
    }
  }

  &[role='button']:hover {
    cursor: pointer;
  }

  &.muted {
    background-color: rgb(255 255 255 / 60%);
    color: var(--neutral-600);
  }

  &.no-border {
    border-color: transparent;
  }

  &.darkIcon[data-i]::before {
    color: var(--neutral-800);
  }
}
</style>
